/* @flow */
import type AssetStore, { TypeAsset } from 'stores/AssetStore';

export type ContactDetails = {
  name: ?string,
  phone: ?string,
};

/**
 * This class helps to obtain collection address and contact
 * details from the asset within the AssetStore.
 */
export default class CaaSAssetCollectionDetails {
  asset: ?TypeAsset;

  constructor(assetStore: AssetStore) {
    this.asset = assetStore.asset;
  }

  /**
   * Returns true if the current CaaS Asset is configured to use a custom collection address.
   */
  isUsingCustomAddress(): boolean {
    return this.asset?.calibration_service?.use_asset_collection_address === false;
  }

  /**
   * Returns true if the current CaaS Asset is configured to use custom contact details.
   */
  isUsingCustomContactDetails(): boolean {
    return this.asset?.calibration_service?.use_asset_contact === false;
  }

  /**
   * Returns the Asset Location's Address.
   */
  getLocationAddress(): ?string {
    if (this.asset?.location?.site?.address) {
      const { address } = this.asset.location.site;

      return `${address.address_1}, ${address.address_2}, ${address.address_4}, ${
        address.postcode
      }`;
    }
  }

  /**
   * Returns the Asset Contact Details.
   */
  getAssetContactDetails(): ContactDetails {
    return {
      name: this.asset?.user.name,
      phone: this.asset?.user.phone,
    };
  }

  /**
   * Returns the custom collection address, if available.
   */
  getCustomAddress() {
    return this.asset?.calibration_service?.collection_address;
  }

  /**
   * Returns the custom contact details.
   */
  getCustomContactDetails(): ContactDetails {
    return {
      name: this.asset?.calibration_service?.contact_name,
      phone: this.asset?.calibration_service?.contact_phone,
    };
  }

  /**
   * Returns the collection address, this can be Asset Location's Address or
   * Custom Collection Address, depending on how the asset is configured.
   */
  getAddress() {
    return this.isUsingCustomAddress() ? this.getCustomAddress() : this.getLocationAddress();
  }

  /**
   * Returns the contact details, it can be Asset Contact Details or
   * Custom Contact Details, depending on how the asset is configured.
   */
  getContactDetails(): ContactDetails {
    return this.isUsingCustomContactDetails()
      ? this.getCustomContactDetails()
      : this.getAssetContactDetails();
  }
}
